import React, { Component } from 'react';
import DraggableSnap from "./DraggableSnap.jsx";
import WordBucket from "./WordBucket.jsx";
import { nextJoke,unsolve,toggle_mute,
  fetchAllJokes,hintClicked,loadJokeById,fetchJokeDetailAndRestart} from "../actions/jokeActions";
import { connect } from "react-redux";
import store from "./store";
import WordPallete from "./WordPallete.jsx";
import SetupHolder from "./SetupHolder.jsx";
import ShareModal from "./ShareModal.jsx";
import AccountModal from "./AccountModal.jsx";
import Header from "./Header.jsx";
import OutOfJokesScreen from "./OutOfJokesScreen.jsx";
import TutorialPopup from "./TutorialPopup.jsx";
import { checkAuthStatus, logout } from "../actions/authActions";
import rimshot from "../badumtss.mp3";
import {wordPlacedSuccess,allWordsPlaced} from "../actions/wordActions";
import { ActionLogTracker } from '../utils/actionLogTracker';
import { logUsageEvent, reportPageLoadPerformance } from '../utils/usageEvents';
import { wordTextAt } from '../utils/wordMatching';


// how long a first-time (or stuck) player can go without touching
// anything before Layout offers the how-to-play popup
const TUTORIAL_POPUP_DELAY_MS = 10000;

class Layout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      share_modal_open: false, account_modal_open: false, tutorial_modal_open: false,
      share_seconds_since_solved: null,
    };
    this.action_log = new ActionLogTracker();
    this.has_interacted = false;
    this.handle_first_interaction = this.handle_first_interaction.bind(this);
    this.handle_first_scroll = this.handle_first_scroll.bind(this);
    this.handle_visibility_change = this.handle_visibility_change.bind(this);
  }

  async componentDidMount() {
    window.addEventListener('scroll', this.handle_first_scroll, { passive: true });
    document.addEventListener('visibilitychange', this.handle_visibility_change);
    reportPageLoadPerformance(this.current_joke_id());
    // independent of the joke queue - doesn't need to block it either way
    this.props.dispatch(checkAuthStatus());
    await this.props.dispatch(fetchAllJokes());
    const requested_id = this.props.match && this.props.match.params.id;
    if (requested_id) {
      const id = Number(requested_id);
      const in_queue = store.getState().jokeReducer.jokes.some(joke => joke.id === id);
      if (in_queue) {
        await this.props.dispatch(loadJokeById(id));
      } else {
        // not in the unseen queue - most likely already solved (e.g. the
        // "Restart" link from /solved/<id>) - fetch it directly instead
        try {
          await this.props.dispatch(fetchJokeDetailAndRestart(id));
        } catch (error) {
          await this.props.dispatch(nextJoke());
        }
      }
    } else {
      await this.props.dispatch(nextJoke());
    }
    this.sync_url_to_current_joke();
    this.action_log.joke_started(this.current_joke_id());
    await this.get_hint()
    this.schedule_tutorial_popup();
  }

  componentWillUnmount() {
    clearTimeout(this.tutorial_timer);
    clearTimeout(this.center_image_timer);
    this.remove_interaction_listeners();
    this.remove_scroll_listener();
    document.removeEventListener('visibilitychange', this.handle_visibility_change);
  }

  // accumulates into this.action_log's hidden-time tracking each time the
  // tab goes hidden then visible again - finalized when a solve is
  // actually logged (see ActionLogTracker.flush)
  handle_visibility_change(){
    this.action_log.handle_visibility_change();
  }

  // a how-to-play popup, offered once if ten seconds pass after the page
  // loads with no sign the player has actually tried anything yet -
  // listens broadly (click/touch/keyboard) rather than off any specific
  // redux action, since the automatic on-mount hint already dispatches a
  // "word placed" action on its own that isn't a real interaction
  schedule_tutorial_popup(){
    document.addEventListener('pointerdown', this.handle_first_interaction);
    document.addEventListener('touchstart', this.handle_first_interaction);
    document.addEventListener('keydown', this.handle_first_interaction);
    this.tutorial_timer = setTimeout(() => {
      if (!this.has_interacted){
        this.setState({ tutorial_modal_open: true });
      }
    }, TUTORIAL_POPUP_DELAY_MS);
  }

  remove_interaction_listeners(){
    document.removeEventListener('pointerdown', this.handle_first_interaction);
    document.removeEventListener('touchstart', this.handle_first_interaction);
    document.removeEventListener('keydown', this.handle_first_interaction);
  }

  handle_first_interaction(){
    if (this.state.tutorial_modal_open){
      // dismissing the popup itself (Got it / X / clicking outside it)
      // isn't actually playing the game - don't let that count
      return;
    }
    this.has_interacted = true;
    clearTimeout(this.tutorial_timer);
    this.remove_interaction_listeners();
  }

  close_tutorial_modal(){
    this.setState({ tutorial_modal_open: false });
    // still hasn't actually played - offer it again if another ten
    // seconds pass with still nothing
    clearTimeout(this.tutorial_timer);
    this.tutorial_timer = setTimeout(() => {
      if (!this.has_interacted){
        this.setState({ tutorial_modal_open: true });
      }
    }, TUTORIAL_POPUP_DELAY_MS);
  }

  // whichever of the first/second punchline word isn't already sitting
  // there from the automatic on-mount hint - no point "hinting" a word
  // the player can already see
  tutorial_hint_word(){
    const words = this.props.punchline_words;
    if (!words.length){
      return { ordinal: 'first', word: null };
    }
    const first_already_filled = this.props.words_correct[0] && words.length > 1;
    const index = first_already_filled ? 1 : 0;
    return { ordinal: index === 0 ? 'first' : 'second', word: words[index].word };
  }

  //keep the address bar showing the joke actually on screen, so every joke
  //has its own shareable/bookmarkable url and a refresh lands back on the
  //same joke instead of resetting to the front of the queue. Uses replace
  //(not push): once a joke is popped off its queue there's no way to
  //reconstruct it, so a "previous" history entry couldn't be restored
  //correctly anyway - pushing would just make the back button misleading.
  sync_url_to_current_joke(){
    const joke_state = store.getState().jokeReducer;
    if (!joke_state.punchline_words.length || !this.props.history){
      return;
    }
    const joke_id = joke_state.punchline_words[0].joke;
    const path = '/joke/' + joke_id;
    if (this.props.location && this.props.location.pathname !== path){
      this.props.history.replace(path);
    }
  }

  async play_again(){
    await this.props.dispatch(fetchAllJokes());
    await this.props.dispatch(nextJoke());
    this.sync_url_to_current_joke();
    this.action_log.joke_started(this.current_joke_id());
    await this.get_hint();
  }

  componentDidUpdate(prevProps) {
    if (this.props.solved == 'visible' && prevProps.solved != 'visible') {
      // the moment the solved screen actually appeared - how long the
      // player lingers here before clicking next/share gets measured
      // against this, not a server timestamp, since neither necessarily
      // happens in the same request as the solve itself
      this.solved_at = Date.now();
      // the original "you solved it" sound, triggered off the real
      // (post-render) solved transition
      if (!this.props.mute) {
        let sound = new Audio(rimshot);
        this.celebration_sound = sound;
        sound.play().catch(() => {});
      }
      // delayed rather than immediate - the last word's own placement
      // (snap into its bucket, color flash) is still visually settling
      // right at this instant, and centering the image in the same breath
      // read as that word's movement shifting it, not as its own distinct
      // "the joke is solved" step
      clearTimeout(this.center_image_timer);
      this.center_image_timer = setTimeout(() => this.center_solved_image(), 500);
    }
  }

  // slides the image into the vertical center of its box - the word
  // palette's individual tiles only go visibility:hidden as they're
  // placed (not display:none), so #drag_words itself still reserves its
  // full height and flexbox's own centering can't close that gap on its
  // own. Collapses #drag_words out of layout first (so the box really is
  // just the image, letting justify-content:center do the rest), then
  // FLIPs: measures the resulting jump and animates from the old position
  // into the new one instead of snapping straight there.
  center_solved_image(){
    const image = document.getElementById('solved_image');
    const words_box = document.getElementById('drag_words');
    if (!image){
      return;
    }
    const before_rect = image.getBoundingClientRect();
    if (words_box){
      words_box.style.display = 'none';
    }
    const after_rect = image.getBoundingClientRect();
    const move_y = before_rect.top - after_rect.top;

    image.style.transitionDuration = '0s';
    image.style.transform = `translateY(${move_y}px)`;
    // flush the jump above before transitioning, else the browser just
    // coalesces both style writes and there's nothing to animate from
    image.getBoundingClientRect();
    image.style.transitionProperty = 'filter, opacity, transform';
    image.style.transitionDuration = '0.4s, 0.4s, 0.5s';
    image.style.transform = 'translateY(0)';
  }

  async get_hint(){
  	// deliberately no re-entrancy guard here: hints can be requested
  	// again before a previous one's 2s slide animation finishes - each
  	// call reads the current words_correct fresh and picks from whatever
  	// remains false, so multiple hints can be in flight/animating at once
  	const words_correct = this.props.words_correct
  	const length = words_correct.filter(x => x === false).length
  	if (length === 0){
  	  return
  	}
  	const indexOfAll = (arr, val) => arr.reduce((acc, el, i) => (el === val ? [...acc, i] : acc), []);
  	const word_to_place_index = Math.floor(Math.random() * length);
  	const word_to_place_number = indexOfAll(words_correct,false)[word_to_place_index]
  	// a repeated word's bucket can end up filled by the *other* matching
  	// tile (isCorrectMatch accepts either) - that leaves this bucket's own
  	// same-index tile already hidden, so look for any still-visible tile
  	// with the right text rather than assuming the same-index one is it
  	const target_text = wordTextAt(this.props.punchline_words, word_to_place_number);
  	const word_to_place = this.props.punchline_words
  	  .map((word, index) => document.getElementById("visible_" + index))
  	  .find((element, index) =>
  	    element && element.style.visibility !== 'hidden'
  	    && wordTextAt(this.props.punchline_words, index) === target_text
  	  )
  	if (!word_to_place){
  	  return
  	}
    word_to_place.style.transitionDuration = "2s"
  	const where_to_place = document.getElementById("invisible_"+ String(word_to_place_number))
  	const destination_rect = where_to_place.getBoundingClientRect();
    const origin_rect = word_to_place.getBoundingClientRect();
    const move_x = destination_rect['x'] - origin_rect['x']
    const move_y = destination_rect['y'] - origin_rect['y']
    word_to_place.style.transform = "translate("+move_x+"px,"+move_y+"px)"
    this.props.dispatch(wordPlacedSuccess(word_to_place_number));
       this.props.dispatch(allWordsPlaced())
    // a plain timer, not ontransitionend: if this hint happens to complete
    // the puzzle, center_solved_image() collapses #drag_words (display:
    // none) 500ms later to recenter the image - an ancestor going
    // display:none cancels any transition still in flight on this tile
    // without ever firing transitionend, which left it stuck visible
    // forever. A timer hides it on schedule regardless of whether the
    // CSS animation itself got cut short.
    setTimeout(() => {
      word_to_place.style.visibility = 'hidden'
    }, 2000)

  }

  async next_joke(){
      this.action_log.flush({
        joke_id: this.current_joke_id(),
        shuffled_words: this.props.shuffled_words,
        time_to_next_seconds: this.solved_at ? (Date.now() - this.solved_at) / 1000 : null,
      });
      this.solved_at = null;
      if (this.celebration_sound){
        this.celebration_sound.pause();
        this.celebration_sound.currentTime = 0;
      }
      // in case Next Joke is clicked within center_solved_image's own
      // delay window, before it's actually fired
      clearTimeout(this.center_image_timer);
      //hide the old (fully sharp) image before swapping in the next joke's
      //data, so the new image's src/blur don't pop in abruptly mid-swap.
      //transitionDuration is zeroed first so this hide is instant rather
      //than fighting the reveal transition for the same 0.4s window.
      const old_image = document.getElementById('solved_image');
      if (old_image){
        old_image.style.transitionDuration = '0s';
        old_image.style.opacity = '0';
        // undo center_solved_image's shift - same DOM node gets reused for
        // the next joke's image, which should start unshifted
        old_image.style.transform = '';
      }
      // undo center_solved_image's collapse of the word palette - the
      // next joke's words need their space back
      const words_box = document.getElementById('drag_words');
      if (words_box){
        words_box.style.display = '';
      }
      await this.props.dispatch(nextJoke());
      this.sync_url_to_current_joke();
      this.action_log.joke_started(this.current_joke_id());
      let words = document.getElementsByClassName("Draggable-Word");
      //return shuffled words to wordpallette
      Array.from(words).forEach(async(word) => {
        word.style.visibility = "visible"
        word.style.transitionDuration = "0s";
        word.style.transform = "";
      })
      let destinations = document.getElementsByClassName("Hidden-Word");
      //make answer invisible
      Array.from(destinations).forEach(async(word) => {
        word.style.color = "transparent"
      })
      await this.props.dispatch(unsolve())
      //to prevent hint moving from wrong position
      await new Promise(r => setTimeout(r, 50));
      //fade the new image back in now that its src/blur have settled
      const new_image = document.getElementById('solved_image');
      if (new_image){
        new_image.style.transitionDuration = '';
        new_image.style.opacity = '';
      }
      await this.get_hint()

  }

  toggle_mute(){
    this.props.dispatch(toggle_mute())
    logUsageEvent('mute_toggled', this.current_joke_id())
  }

  current_joke_id(){
    return this.props.punchline_words.length ? this.props.punchline_words[0].joke : null;
  }

  // fires once per page load, the first time the player scrolls at all -
  // a signal the page didn't comfortably fit their viewport as-is
  handle_first_scroll(){
    this.remove_scroll_listener();
    logUsageEvent('first_scroll', this.current_joke_id())
  }

  remove_scroll_listener(){
    window.removeEventListener('scroll', this.handle_first_scroll);
  }

  open_share_modal(){
    const seconds_since_solved = this.solved_at ? (Date.now() - this.solved_at) / 1000 : null;
    this.setState({ share_modal_open: true, share_seconds_since_solved: seconds_since_solved });
  }

  on_hint_click(){
    //distinct from get_hint() being called automatically on mount/next_joke -
    //this only fires for an actual user click on the button, for logging
    this.props.dispatch(hintClicked());
    this.get_hint();
  }

  render() {
  	let { error, shuffled_words, punchline_words } = this.props;
    if (error) {
  	  return <div>Error! {error.message}</div>;
    }
    let action_button;
    if (this.props.solved == 'visible'){
      action_button = <div id='solved_actions'>
        <button id='next_joke' onClick={() => this.next_joke()}>
        Click for next Joke</button>
        <button id='share_joke' onClick={() => this.open_share_modal()}>
        Share 📤</button>
      </div>
    }
    else{
      action_button = <button id='get_hint' onClick={() =>  this.on_hint_click()}>Get Hint? 🤔</button>
    }
    const words_correct = this.props.words_correct;
    const progress = words_correct.length
      ? words_correct.filter(Boolean).length / words_correct.length
      : 0;
    const image_blur = 20 * (1 - progress);
    let subtitle;
    let content_button;
    if (this.props.out_of_jokes){
      content_button = <OutOfJokesScreen onPlayAgain={() => this.play_again()} />
    }
    else{
      subtitle = <p className="subtitle">The joke can be answered by re-arranging the words below,
        drag them to the empty positions</p>
      content_button =
      <div id='game_content'>
        <SetupHolder setup={this.props.setup}/>
        <WordPallete punchline_words={shuffled_words} image={this.props.image} imageBlur={image_blur}/>
        {action_button }
        <div id='destinationElements'>
          {punchline_words.map(word=> {
            return (
              <WordBucket key={word.order} word={word.word} order={'invisible_' + word.order}
              word_order={word.order}/>
              );
            })
          }
        </div>
      </div>
    }
    return (
      <div id="page">
        <Header
          auth_checked={this.props.auth_checked}
          authenticated={this.props.authenticated}
          auth_email={this.props.auth_email}
          mute={this.props.mute}
          onLogout={() => this.props.dispatch(logout())}
          onOpenLogin={() => this.setState({account_modal_open: true})}
          onToggleMute={() => this.toggle_mute()}
        />
        {subtitle}
        <div id="game_live_region" role="status" aria-live="polite" className="sr_only">
          {this.props.announcement}
        </div>
        {content_button}
        {this.state.share_modal_open &&
          <ShareModal
            payload={{
              joke: this.props.punchline_words.length ? this.props.punchline_words[0].joke : null,
              seconds_since_solved: this.state.share_seconds_since_solved,
            }}
            onClose={() => this.setState({share_modal_open: false})}
          />
        }
        {this.state.account_modal_open &&
          <AccountModal onClose={() => this.setState({account_modal_open: false})} />
        }
        {this.state.tutorial_modal_open &&
          <TutorialPopup hint={this.tutorial_hint_word()} onClose={() => this.close_tutorial_modal()} />
        }
    	</div>
    )
  }
}


const mapStateToProps = state => ({
  error: state.jokeReducer.error,
  words_correct:state.jokeReducer.words_correct,
  solved:state.jokeReducer.solved,
  setup:state.jokeReducer.setup,
  image:state.jokeReducer.image,
  punchline_words:state.jokeReducer.punchline_words,
  shuffled_words:state.jokeReducer.shuffled_words,
  mute: state.jokeReducer.mute,
  out_of_jokes: state.jokeReducer.out_of_jokes,
  announcement: state.jokeReducer.announcement,
  auth_checked: state.authReducer.checked,
  authenticated: state.authReducer.authenticated,
  auth_email: state.authReducer.email,
});

export default connect(mapStateToProps)(Layout);
